// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const DEMO_COLORS = [
  { name: 'Primary', hex: '#d4a843', usage: 'Buttons, CTAs, highlights' },
  { name: 'Secondary', hex: '#1a1a2e', usage: 'Backgrounds, cards' },
  { name: 'Accent', hex: '#e74c3c', usage: 'Alerts, urgency' },
  { name: 'Text', hex: '#f0f0f0', usage: 'Body text, headings' },
  { name: 'Muted', hex: '#888888', usage: 'Captions, placeholders' },
  { name: 'Success', hex: '#4caf50', usage: 'Success states, active' },
];

const DEMO_FONTS = [
  { name: 'Playfair Display', weight: 'Bold', usage: 'Headlines, hero text', size: '28-48px', sample: 'The quick brown fox' },
  { name: 'Inter', weight: 'Regular', usage: 'Body text, descriptions', size: '14-16px', sample: 'The quick brown fox jumps over the lazy dog' },
  { name: 'Inter', weight: 'Semibold', usage: 'Subheadings, labels', size: '16-20px', sample: 'Subheading text style' },
  { name: 'JetBrains Mono', weight: 'Regular', usage: 'Code, data, numbers', size: '13px', sample: 'const value = 42;' },
];

const BRAND_VOICE = [
  { trait: 'Confident', desc: 'Assertive without arrogance. We know our value.', do: 'We deliver results.', dont: 'We think we might be able to help.' },
  { trait: 'Professional', desc: 'Polished and trustworthy at every touchpoint.', do: 'Our platform ensures data integrity.', dont: 'Our stuff works pretty good.' },
  { trait: 'Innovative', desc: 'Forward-thinking, not buzzwordy.', do: 'AI-powered analysis in real-time.', dont: 'Disruptive synergy leveraging blockchain.' },
  { trait: 'Direct', desc: 'Clear and concise. Respect people\'s time.', do: 'Start your free trial.', dont: 'Why not take a moment to consider starting...' },
];

const S = {
  page: { padding: '24px', maxWidth: 1200, margin: '0 auto' },
  title: { fontSize: 28, fontWeight: 700, color: 'var(--text)', margin: 0 },
  subtitle: { fontSize: 14, color: '#999', marginTop: 4 },
  tabs: { display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' },
  tab: (active) => ({ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, background: active ? 'var(--accent, #d4a843)' : 'var(--card, #1a1a2e)', color: active ? '#000' : 'var(--text)' }),
  card: { background: 'var(--card, #1a1a2e)', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid var(--border, #2a2a4a)' },
  cardTitle: { fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 12 },
  badge: (color) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${color}22`, color }),
  btn: (primary) => ({ padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, background: primary ? 'var(--accent, #d4a843)' : 'var(--card, #1a1a2e)', color: primary ? '#000' : 'var(--text)', border: primary ? 'none' : '1px solid var(--border, #2a2a4a)' }),
  input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border, #2a2a4a)', background: 'var(--bg, #0f0f23)', color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' },
  colorSwatch: (hex) => ({ width: 60, height: 60, borderRadius: 12, background: hex, border: '2px solid rgba(255,255,255,0.1)', cursor: 'pointer' }),
};

function BrandKitPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('colors');
  const [colors, setColors] = useState(DEMO_COLORS);
  const [editingColor, setEditingColor] = useState(null);

  const TABS = [
    { id: 'colors', label: 'Colors', icon: '🎨' },
    { id: 'typography', label: 'Typography', icon: '🔤' },
    { id: 'logos', label: 'Logo Files', icon: '⭐' },
    { id: 'voice', label: 'Brand Voice', icon: '🗣️' },
    { id: 'templates', label: 'Templates', icon: '📐' },
    { id: 'guidelines', label: 'Guidelines', icon: '📖' },
  ];

  return (
    <div style={S.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={S.title}>🎨 Brand Kit</h1>
          <p style={S.subtitle}>Your complete brand identity — colors, fonts, logos, voice, and guidelines in one place</p>
        </div>
        <button style={S.btn(true)}>📥 Download Brand Kit</button>
      </div>

      <div style={S.tabs}>
        {TABS.map(t => (
          <button key={t.id} style={S.tab(tab === t.id)} onClick={() => setTab(t.id)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Colors Tab */}
      {tab === 'colors' && (
        <div>
          <div style={S.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={S.cardTitle}>Brand Colors</div>
              <button style={{ ...S.btn(false), fontSize: 12 }}>+ Add Color</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
              {colors.map((c, i) => (
                <div key={i} style={{ borderRadius: 12, border: '1px solid var(--border, #2a2a4a)', overflow: 'hidden', cursor: 'pointer' }} onClick={() => setEditingColor(i)}>
                  <div style={{ height: 80, background: c.hex }} />
                  <div style={{ padding: 12 }}>
                    <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>{c.name}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 13, color: '#888', marginBottom: 4 }}>{c.hex}</div>
                    <div style={{ fontSize: 11, color: '#666' }}>{c.usage}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Color Palette Preview</div>
            <div style={{ display: 'flex', borderRadius: 12, overflow: 'hidden', height: 60, marginBottom: 16 }}>
              {colors.map((c, i) => (
                <div key={i} style={{ flex: 1, background: c.hex, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: i <= 1 || i === 4 ? '#fff' : '#000' }}>{c.hex}</div>
              ))}
            </div>
            <div style={{ fontSize: 13, color: '#888' }}>Tip: Ensure sufficient contrast ratios between text and background colors for accessibility (WCAG 2.1 AA requires 4.5:1 for normal text).</div>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>AI Color Harmony Suggestions</div>
            <p style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>Based on your primary brand color, here are complementary palette options:</p>
            {[
              { name: 'Analogous', colors: ['#d4a843', '#d4c543', '#d49043'], desc: 'Warm, harmonious feel' },
              { name: 'Complementary', colors: ['#d4a843', '#4376d4', '#436cd4'], desc: 'Bold, high contrast' },
              { name: 'Triadic', colors: ['#d4a843', '#43d4a8', '#a843d4'], desc: 'Vibrant, balanced' },
            ].map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 0', borderBottom: i < 2 ? '1px solid var(--border, #2a2a4a)' : 'none' }}>
                <span style={{ width: 100, fontWeight: 600, color: 'var(--text)', fontSize: 13 }}>{p.name}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  {p.colors.map((c, j) => <div key={j} style={{ width: 40, height: 40, borderRadius: 8, background: c, border: '1px solid rgba(255,255,255,0.1)' }} />)}
                </div>
                <span style={{ fontSize: 12, color: '#888' }}>{p.desc}</span>
                <button style={{ ...S.btn(false), padding: '4px 10px', fontSize: 11, marginLeft: 'auto' }}>Apply</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Typography Tab */}
      {tab === 'typography' && (
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}>Font Library</div>
            {DEMO_FONTS.map((f, i) => (
              <div key={i} style={{ padding: '16px 0', borderBottom: i < DEMO_FONTS.length - 1 ? '1px solid var(--border, #2a2a4a)' : 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div>
                    <span style={{ fontWeight: 700, color: 'var(--text)', marginRight: 8 }}>{f.name}</span>
                    <span style={S.badge('#2196f3')}>{f.weight}</span>
                    <span style={{ fontSize: 11, color: '#888', marginLeft: 8 }}>{f.size}</span>
                  </div>
                  <span style={{ fontSize: 12, color: '#888' }}>{f.usage}</span>
                </div>
                <div style={{ fontFamily: f.name === 'JetBrains Mono' ? 'monospace' : f.name, fontWeight: f.weight === 'Bold' ? 700 : f.weight === 'Semibold' ? 600 : 400, fontSize: f.name === 'Playfair Display' ? 28 : f.name === 'JetBrains Mono' ? 13 : f.weight === 'Semibold' ? 18 : 15, color: 'var(--text)' }}>
                  {f.sample}
                </div>
              </div>
            ))}
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Type Scale</div>
            {[
              { label: 'H1 — Page Title', size: 32, weight: 700, font: 'Playfair Display' },
              { label: 'H2 — Section Title', size: 24, weight: 700, font: 'Playfair Display' },
              { label: 'H3 — Subsection', size: 18, weight: 600, font: 'Inter' },
              { label: 'Body — Regular', size: 15, weight: 400, font: 'Inter' },
              { label: 'Caption — Small', size: 12, weight: 400, font: 'Inter' },
              { label: 'Code — Monospace', size: 13, weight: 400, font: 'monospace' },
            ].map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 16, padding: '10px 0', borderBottom: '1px solid var(--border, #2a2a4a)' }}>
                <span style={{ width: 160, fontSize: 11, color: '#888' }}>{t.label}</span>
                <span style={{ fontFamily: t.font, fontSize: t.size, fontWeight: t.weight, color: 'var(--text)' }}>The Forge Standard</span>
                <span style={{ fontSize: 11, color: '#888', marginLeft: 'auto' }}>{t.size}px / {t.weight}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Logo Files Tab */}
      {tab === 'logos' && (
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}>Logo Variations</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
              {[
                { name: 'Primary Logo', bg: '#0f0f23', desc: 'Full logo on dark', format: 'SVG, PNG' },
                { name: 'Light Version', bg: '#ffffff', desc: 'Full logo on light', format: 'SVG, PNG' },
                { name: 'Icon Only', bg: '#0f0f23', desc: 'Favicon / app icon', format: 'SVG, PNG, ICO' },
                { name: 'Wordmark', bg: '#0f0f23', desc: 'Text-only version', format: 'SVG, PNG' },
                { name: 'Monochrome', bg: '#333', desc: 'Single color version', format: 'SVG, PNG' },
                { name: 'Stacked', bg: '#0f0f23', desc: 'Vertical layout', format: 'SVG, PNG' },
              ].map((logo, i) => (
                <div key={i} style={{ borderRadius: 12, border: '1px solid var(--border, #2a2a4a)', overflow: 'hidden' }}>
                  <div style={{ height: 100, background: logo.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 20, fontWeight: 700, color: logo.bg === '#ffffff' ? '#1a1a2e' : 'var(--accent, #d4a843)', fontFamily: 'serif' }}>TFS</span>
                  </div>
                  <div style={{ padding: 12 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{logo.name}</div>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>{logo.desc}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 10, color: '#666' }}>{logo.format}</span>
                      <button style={{ ...S.btn(false), padding: '2px 8px', fontSize: 11 }}>📥 Download</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Logo Usage Rules</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#4caf50', marginBottom: 8 }}>Do</div>
                {['Use on clean, uncluttered backgrounds', 'Maintain minimum clear space around logo', 'Use approved color variations only', 'Scale proportionally'].map((d, i) => (
                  <div key={i} style={{ padding: '6px 0', fontSize: 13, color: 'var(--text)' }}>✅ {d}</div>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#f44336', marginBottom: 8 }}>Don't</div>
                {['Stretch or distort the logo', 'Change the logo colors', 'Place on busy backgrounds', 'Add effects (shadows, gradients)'].map((d, i) => (
                  <div key={i} style={{ padding: '6px 0', fontSize: 13, color: 'var(--text)' }}>❌ {d}</div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <button style={S.btn(true)}>📦 Download All Logos (ZIP)</button>
            <button style={S.btn(false)}>📤 Upload New Logo</button>
          </div>
        </div>
      )}

      {/* Brand Voice Tab */}
      {tab === 'voice' && (
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}>Brand Voice & Tone</div>
            <p style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>Define how your brand communicates across all channels. This guides AI content generation and team messaging.</p>
            {BRAND_VOICE.map((v, i) => (
              <div key={i} style={{ padding: '16px 0', borderBottom: i < BRAND_VOICE.length - 1 ? '1px solid var(--border, #2a2a4a)' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent, #d4a843)' }}>{v.trait}</span>
                </div>
                <div style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>{v.desc}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(76, 175, 80, 0.08)', border: '1px solid rgba(76, 175, 80, 0.2)' }}>
                    <span style={{ fontSize: 11, color: '#4caf50', fontWeight: 600 }}>DO:</span>
                    <span style={{ fontSize: 13, color: 'var(--text)', marginLeft: 6 }}>"{v.do}"</span>
                  </div>
                  <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(244, 67, 54, 0.08)', border: '1px solid rgba(244, 67, 54, 0.2)' }}>
                    <span style={{ fontSize: 11, color: '#f44336', fontWeight: 600 }}>DON'T:</span>
                    <span style={{ fontSize: 13, color: 'var(--text)', marginLeft: 6 }}>"{v.dont}"</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Taglines & Slogans</div>
            {[
              { line: 'Build. Launch. Dominate.', usage: 'Primary tagline' },
              { line: 'Your complete digital business suite.', usage: 'Descriptive tagline' },
              { line: 'From idea to empire.', usage: 'Aspirational tagline' },
            ].map((t, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < 2 ? '1px solid var(--border, #2a2a4a)' : 'none' }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', fontStyle: 'italic' }}>"{t.line}"</span>
                <span style={{ fontSize: 12, color: '#888' }}>{t.usage}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Templates Tab */}
      {tab === 'templates' && (
        <div style={S.card}>
          <div style={S.cardTitle}>Brand Templates</div>
          <p style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>Pre-designed templates using your brand colors, fonts, and logo.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
            {[
              { name: 'Business Card', icon: '🃏', desc: 'Standard 3.5" x 2"' },
              { name: 'Letterhead', icon: '📄', desc: 'A4 & Letter size' },
              { name: 'Invoice', icon: '📋', desc: 'Professional invoice' },
              { name: 'Email Signature', icon: '📧', desc: 'HTML email sig' },
              { name: 'Social Banner', icon: '🖼️', desc: 'All platform sizes' },
              { name: 'Presentation', icon: '📊', desc: '16:9 slide deck' },
              { name: 'Flyer', icon: '📰', desc: '8.5" x 11" print' },
              { name: 'Proposal', icon: '📑', desc: 'Client proposal doc' },
            ].map((t, i) => (
              <div key={i} style={{ padding: 16, borderRadius: 10, border: '1px solid var(--border, #2a2a4a)', textAlign: 'center', cursor: 'pointer' }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>{t.icon}</div>
                <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{t.name}</div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>{t.desc}</div>
                <button style={{ ...S.btn(false), fontSize: 11, padding: '6px 14px' }}>📥 Download</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Guidelines Tab */}
      {tab === 'guidelines' && (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={S.cardTitle}>Brand Guidelines Document</div>
            <button style={S.btn(true)}>📥 Export PDF</button>
          </div>
          <p style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>Auto-generated brand guidelines based on your Brand Kit settings. Updates in real-time as you modify your brand elements.</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {[
              { section: '1. Brand Overview', pages: '2-3', desc: 'Mission, vision, values, brand story' },
              { section: '2. Logo Usage', pages: '4-7', desc: 'Variations, clear space, minimum size, don\'ts' },
              { section: '3. Color Palette', pages: '8-9', desc: 'Primary, secondary, accent colors with codes' },
              { section: '4. Typography', pages: '10-11', desc: 'Font families, hierarchy, sizing' },
              { section: '5. Brand Voice', pages: '12-13', desc: 'Tone, vocabulary, do\'s and don\'ts' },
              { section: '6. Templates', pages: '14-18', desc: 'Business cards, letterhead, social, email' },
            ].map((s, i) => (
              <div key={i} style={{ padding: '12px 16px', borderRadius: 8, border: '1px solid var(--border, #2a2a4a)' }}>
                <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{s.section}</div>
                <div style={{ fontSize: 12, color: '#888' }}>Pages {s.pages} — {s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default BrandKitPage;
